import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNoMotion } from "@/lib/a11yPrefs";

// Rotates through SEO keyword phrases with a smooth fade/slide. Used in the
// hero H1 so each state surfaces a strong keyword (חשבוניות ירוקות / דיגיטליות
// / אונליין ...) while the page also carries them statically for crawlers.
// CLS-safe: an invisible sizer reserves the WIDEST word's space and the animated
// word is positioned absolutely on top — so changing words never re-wraps the
// headline or shifts the content below it (Cumulative Layout Shift = 0).
export default function RotatingText({ words, interval = 2400, className = "" }) {
  const [i, setI] = React.useState(0);
  const noMotion = useNoMotion();

  React.useEffect(() => {
    // "עצירת אנימציות" — הלולאה מונעת ב-JS, ולכן CSS ו-framer לא נוגעים בה.
    // במצב עצירה מציגים מילה אחת קבועה במקום להחליף.
    if (noMotion) { setI(0); return; }
    const t = setInterval(() => setI((p) => (p + 1) % words.length), interval);
    return () => clearInterval(t);
  }, [words.length, interval, noMotion]);

  const longest = words.reduce((a, b) => (b.length > a.length ? b : a), words[0] || "");

  return (
    <span className="relative inline-block align-bottom whitespace-nowrap">
      {/* sizer — reserves the widest word's box; invisible but takes space */}
      <span className={`invisible ${className}`} aria-hidden="true">{longest}</span>
      {/* Crossfade (NO mode="wait") so a word is ALWAYS visible — the old word
          fades/slides out while the new one fades/slides in, so the headline is
          never left blank mid-transition. initial={false} = the first word shows
          instantly on mount (matches the static prerendered hero, no enter fade). */}
      <AnimatePresence initial={false}>
        <motion.span
          key={i}
          initial={{ opacity: 0, y: "0.35em" }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: "-0.35em" }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className={`absolute right-0 top-0 whitespace-nowrap ${className}`}
        >
          {words[i]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}
